import { motion } from 'framer-motion';
import { Linkedin, Briefcase, GraduationCap } from 'lucide-react';
import { IMAGES } from '../../constants/images';

const leaders = [
  {
    name: 'CA Abhinav Kapoor',
    role: 'Finance Vertical Head',
    image: IMAGES.abhinav,
    linkedin: 'https://www.linkedin.com/in/ca-abhinav-kapoor-aborwar-2b083755/',
    qualifications: ['Chartered Accountant (2012)', 'Law Graduate, University of Rajasthan'],
    experience: [
      'Nearly a decade in Finance Sector',
      'Credit Manager at ICICI Bank (3 years)',
      'Expertise in SME Funding & Project Loans'
    ],
    description: 'Abhinav leads our Finance vertical with deep expertise in banking and credit assessment. His experience at ICICI Bank combined with his CA and legal background provides a unique perspective in structuring complex financing solutions for our clients.',
    expertise: ['SME Funding', 'Project Finance', 'Credit Assessment', 'Term Loans']
  },
  {
    name: 'CA Aneesh Patni',
    role: 'Subsidy Vertical Head',
    image: IMAGES.aneesh,
    linkedin: 'https://www.linkedin.com/in/aneesh-patni-88aborwar/',
    qualifications: ['Chartered Accountant (2012)', 'B.Com, University of Rajasthan'],
    experience: [
      'ERP Consultant (3 years)',
      'Expert in State & Central Government Subsidies',
      'Leading all Government Benefits initiatives'
    ],
    description: 'Aneesh heads our Subsidy vertical, successfully navigating both State and Central Government schemes. His background in ERP consulting and finance enables him to provide structured, systematic guidance to enterprises seeking government benefits.',
    expertise: ['Government Subsidies', 'RIPS', 'MSME Schemes', 'Central Schemes']
  },
  {
    name: 'Mr. Jeetesh Dutt Mathur',
    role: 'Marketing Vertical Head',
    image: IMAGES.jeetesh,
    linkedin: 'https://www.linkedin.com/in/jeetesh-mathur-3a6b4441/',
    qualifications: ['B.Com, University of Rajasthan'],
    experience: [
      '15+ years in Retail Finance',
      'Sales Head at Citi Financial (7 years)',
      'AVP at Andromeda (8 years)'
    ],
    description: 'Jeetesh brings over 15 years of retail finance expertise to our team. His extensive network across banks and NBFCs, built during his tenure at Citi Financial and Andromeda, enables us to offer clients the best retail loan options available.',
    expertise: ['Retail Loans', 'Banking Relations', 'Sales Strategy', 'NBFC Partnerships']
  }
];

export const LeadershipProfiles = () => (
  <section className="py-24" data-testid="leader-profiles">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="space-y-24">
        {leaders.map((leader, index) => (
          <motion.div
            key={leader.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
          >
            <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#3b5998]/20 to-[#00bcd4]/20 rounded-3xl blur-xl"></div>
                <img
                  src={leader.image}
                  alt={leader.name}
                  className="relative rounded-2xl shadow-xl w-full max-w-md mx-auto"
                />
              </div>
            </div>
            <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3b5998]/10 to-[#00bcd4]/10 rounded-full mb-4">
                <Briefcase size={16} className="text-[#3b5998]" />
                <span className="text-sm font-medium text-[#3b5998]">{leader.role}</span>
              </div>
              <h2 className="font-heading text-3xl font-semibold text-[#0f172a] mb-4">
                {leader.name}
              </h2>
              <p className="text-[#64748b] leading-relaxed mb-6">
                {leader.description}
              </p>

              <div className="mb-6">
                <h4 className="font-semibold text-[#0f172a] mb-3 flex items-center gap-2">
                  <GraduationCap size={18} className="text-[#00bcd4]" />
                  Qualifications
                </h4>
                <ul className="space-y-2">
                  {leader.qualifications.map((qual, i) => (
                    <li key={i} className="text-[#64748b] text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00bcd4]"></span>
                      {qual}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-[#0f172a] mb-3 flex items-center gap-2">
                  <Briefcase size={18} className="text-[#00bcd4]" />
                  Experience
                </h4>
                <ul className="space-y-2">
                  {leader.experience.map((exp, i) => (
                    <li key={i} className="text-[#64748b] text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3b5998]"></span>
                      {exp}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {leader.expertise.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-[#f1f5f9] rounded-full text-xs font-medium text-[#64748b]"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <a
                href={leader.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`linkedin-${leader.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0077b5] text-white rounded-full text-sm font-medium hover:bg-[#006097] transition-colors"
              >
                <Linkedin size={18} />
                Connect on LinkedIn
              </a>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
